import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {App, Button, Col, Form, Input, Row, Select, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {buildDeleteConfirmation} from "../../../Components/DeleteConfirmation";
import {PageHeader, Panel} from "../../../Components/ui";
import {
    EventHotelReturnDto,
    EventRoomTypeReturnDto,
    useDeleteAccommodationOptionsMutation,
    useEventHotelsListQuery,
    useEventRoomAllotmentsListQuery,
    useEventRoomTypesListQuery,
    useHotelCatalogQuery,
    useSaveAccommodationOptionMutation
} from "../../../GraphQL/generated/graphql";
import {getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {formatLocalDate} from "../adminDateFormat";
import {formatOptionalLocalDateInput, formatRequiredLocalDateInput} from "../roomAllotmentFormDates";
import {dateSorter, numberSorter, textSorter} from "../tableSorters";
import {HotelCatalogModal} from "./HotelCatalogModal";
import {
    AccommodationOptionFormState,
    AccommodationOptionRow,
    CATALOG_HOTEL_SELECTION_PREFIX,
    createDefaultAccommodationOptionForm,
    dateInputValue,
    EVENT_HOTEL_SELECTION_PREFIX,
    mergeById,
    mergeEventHotels,
    parseAccommodationHotelSelection,
    textValue
} from "./eventConfigPayloads";
import {
    HotelIdentityCatalogItem
} from "./eventConfigQueries";
import {useEventBasicSetup, useEventConfigScope} from "./useEventConfigScope";
import "./eventConfig.css";

const {Text} = Typography;

// Event workspace "accommodation": event hotels, room types and allotments edited as one option row.
const EventAccommodationPage: FC = () => {
    const {message, modal} = App.useApp();
    const {t} = useTranslation();
    const {eventNumericId, errorLabels, baseWhere} = useEventConfigScope();
    const {event: selectedBasicSetupEvent} = useEventBasicSetup(eventNumericId);
    const [inlineEventHotels, setInlineEventHotels] = useState<EventHotelReturnDto[]>([]);
    const [isAccommodationOptionFormOpen, setIsAccommodationOptionFormOpen] = useState(false);
    const [accommodationOptionForm, setAccommodationOptionForm] = useState<AccommodationOptionFormState>(
        () => createDefaultAccommodationOptionForm()
    );
    const accommodationOptionFormRef = useRef<HTMLDivElement>(null);
    const shouldScrollToAccommodationOptionFormRef = useRef(false);
    const [selectedAccommodationOptionRowIds, setSelectedAccommodationOptionRowIds] = useState<React.Key[]>([]);
    const [isHotelCatalogFormOpen, setIsHotelCatalogFormOpen] = useState(false);

    useEffect(() => {
        setInlineEventHotels([]);
        setIsAccommodationOptionFormOpen(false);
        setAccommodationOptionForm(createDefaultAccommodationOptionForm());
        setSelectedAccommodationOptionRowIds([]);
    }, [eventNumericId]);

    const hotelsQuery = useEventHotelsListQuery({variables: {take: 50, where: baseWhere()}});
    const eventHotelsForDisplay = useMemo(
        () => mergeEventHotels(hotelsQuery.data?.eventHotels?.items ?? [], inlineEventHotels),
        [hotelsQuery.data?.eventHotels?.items, inlineEventHotels]
    );
    const hotelIdentityCatalogQuery = useHotelCatalogQuery({
        variables: {
            eventId: eventNumericId ?? 0,
            take: 100
        },
        skip: !eventNumericId
    });
    const hotelIds = useMemo(
        () => eventHotelsForDisplay.map(h => h.id),
        [eventHotelsForDisplay]
    );
    const roomTypesWhere = useMemo(() => {
        if (!eventNumericId) {
            return undefined;
        }

        return {hotelId: {in: hotelIds.length ? hotelIds : [-1]}};
    }, [eventNumericId, hotelIds]);
    const roomTypesQuery = useEventRoomTypesListQuery({
        variables: {
            take: 50,
            where: roomTypesWhere
        }
    });
    const eventRoomTypesForDisplay = useMemo(
        () => mergeById(roomTypesQuery.data?.eventRoomTypes?.items ?? [], []),
        [roomTypesQuery.data?.eventRoomTypes?.items]
    );
    const roomTypeIds = useMemo(
        () => eventRoomTypesForDisplay.map(rt => rt.id),
        [eventRoomTypesForDisplay]
    );
    const allotmentsWhere = useMemo(() => {
        if (!eventNumericId) {
            return undefined;
        }

        return {
            and: [
                {hotelId: {in: hotelIds.length ? hotelIds : [-1]}},
                {roomTypeId: {in: roomTypeIds.length ? roomTypeIds : [-1]}}
            ]
        };
    }, [eventNumericId, hotelIds, roomTypeIds]);
    const allotmentsQuery = useEventRoomAllotmentsListQuery({
        variables: {
            take: 50,
            where: allotmentsWhere
        }
    });
    const eventRoomAllotmentsForDisplay = useMemo(
        () => mergeById(allotmentsQuery.data?.eventRoomAllotments?.items ?? [], []),
        [allotmentsQuery.data?.eventRoomAllotments?.items]
    );

    const [saveAccommodationOption, {loading: savingAccommodationOption}] = useSaveAccommodationOptionMutation();
    const [deleteAccommodationOptions, {loading: deletingAccommodationOptions}] = useDeleteAccommodationOptionsMutation();

    const hotelIdentityCatalogItems = useMemo(
        () => (hotelIdentityCatalogQuery.data?.hotelIdentityCatalog?.items ?? [])
            .filter((item): item is HotelIdentityCatalogItem => !!item),
        [hotelIdentityCatalogQuery.data?.hotelIdentityCatalog?.items]
    );
    const hotelNamesById = useMemo(() => {
        const map = new Map<number, string>();
        eventHotelsForDisplay.forEach(h => {
            map.set(h.id, h.name);
        });
        return map;
    }, [eventHotelsForDisplay]);
    const roomTypeNamesById = useMemo(() => {
        const map = new Map<number, string>();
        eventRoomTypesForDisplay.forEach(rt => {
            if (rt) {
                map.set(rt.id, rt.name);
            }
        });
        return map;
    }, [eventRoomTypesForDisplay]);
    const roomTypesById = useMemo(() => {
        const map = new Map<number, EventRoomTypeReturnDto>();
        eventRoomTypesForDisplay.forEach(roomType => {
            map.set(roomType.id, roomType);
        });
        return map;
    }, [eventRoomTypesForDisplay]);
    const unavailableHotelLabel = t("event-config-hotel-unavailable", {defaultValue: "Hotel unavailable"});
    const unavailableRoomTypeLabel = t("event-config-room-type-unavailable", {defaultValue: "Room type unavailable"});
    const getHotelDisplayLabel = useCallback((hotelId?: number | null) => {
        if (hotelId == null) {
            return unavailableHotelLabel;
        }

        return hotelNamesById.get(hotelId) ?? unavailableHotelLabel;
    }, [hotelNamesById, unavailableHotelLabel]);
    const getRoomTypeDisplayLabel = useCallback((roomTypeId?: number | null) => {
        if (roomTypeId == null) {
            return unavailableRoomTypeLabel;
        }

        return roomTypeNamesById.get(roomTypeId) ?? unavailableRoomTypeLabel;
    }, [roomTypeNamesById, unavailableRoomTypeLabel]);

    const accommodationHotelSelectionOptions = useMemo(() => [
        ...eventHotelsForDisplay.map(hotel => ({
            value: `${EVENT_HOTEL_SELECTION_PREFIX}${hotel.id}`,
            label: [hotel.name, [hotel.city, hotel.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")
        })),
        ...hotelIdentityCatalogItems.map(item => ({
            value: `${CATALOG_HOTEL_SELECTION_PREFIX}${item.id}`,
            label: [
                item.name,
                [item.city, item.country].filter(Boolean).join(", "),
                t("event-config-hotel-catalog-choice", {defaultValue: "catalog"})
            ].filter(Boolean).join(" · ")
        }))
    ], [eventHotelsForDisplay, hotelIdentityCatalogItems, t]);

    const getDefaultAccommodationHotelSelection = useCallback(() => {
        if (eventHotelsForDisplay[0]) {
            return `${EVENT_HOTEL_SELECTION_PREFIX}${eventHotelsForDisplay[0].id}`;
        }

        if (hotelIdentityCatalogItems[0]) {
            return `${CATALOG_HOTEL_SELECTION_PREFIX}${hotelIdentityCatalogItems[0].id}`;
        }

        return "";
    }, [eventHotelsForDisplay, hotelIdentityCatalogItems]);

    const accommodationOptionRows = useMemo<AccommodationOptionRow[]>(() => {
        const rowsFromAllotments = eventRoomAllotmentsForDisplay.map(allotment => {
            const roomType = roomTypesById.get(allotment.roomTypeId);

            return {
                rowId: `allotment-${allotment.id}`,
                allotmentId: allotment.id,
                roomTypeId: allotment.roomTypeId,
                hotelId: allotment.hotelId,
                hotelName: getHotelDisplayLabel(allotment.hotelId),
                roomTypeName: roomType?.name ?? getRoomTypeDisplayLabel(allotment.roomTypeId),
                capacity: roomType?.capacity ?? "",
                pricePerNight: roomType?.pricePerNight ?? "",
                description: roomType?.description ?? "",
                startDate: allotment.startDate,
                endDate: allotment.endDate,
                totalRooms: allotment.totalRooms,
                paymentDeadline: allotment.paymentDeadline,
                reservationCount: roomType?.reservationCount ?? 0
            };
        });
        const allocatedRoomTypeIds = new Set(eventRoomAllotmentsForDisplay.map(allotment => allotment.roomTypeId));
        const rowsFromRoomTypesWithoutAllotment = eventRoomTypesForDisplay
            .filter(roomType => !allocatedRoomTypeIds.has(roomType.id))
            .map(roomType => ({
                rowId: `room-type-${roomType.id}`,
                roomTypeId: roomType.id,
                hotelId: roomType.hotelId,
                hotelName: getHotelDisplayLabel(roomType.hotelId),
                roomTypeName: roomType.name,
                capacity: roomType.capacity,
                pricePerNight: roomType.pricePerNight,
                description: roomType.description,
                startDate: "",
                endDate: "",
                totalRooms: "",
                paymentDeadline: null,
                reservationCount: roomType.reservationCount ?? 0
            }));

        return [...rowsFromAllotments, ...rowsFromRoomTypesWithoutAllotment].sort((a, b) =>
            a.hotelName.localeCompare(b.hotelName, undefined, {sensitivity: "base"}) ||
            a.roomTypeName.localeCompare(b.roomTypeName, undefined, {sensitivity: "base"}) ||
            a.rowId.localeCompare(b.rowId)
        );
    }, [
        eventRoomAllotmentsForDisplay,
        eventRoomTypesForDisplay,
        getHotelDisplayLabel,
        getRoomTypeDisplayLabel,
        roomTypesById
    ]);

    const selectedAccommodationOptionRows = useMemo(
        () => accommodationOptionRows.filter(row => selectedAccommodationOptionRowIds.includes(row.rowId)),
        [accommodationOptionRows, selectedAccommodationOptionRowIds]
    );

    const editedAccommodationOptionRow = useMemo(() => {
        if (!accommodationOptionForm.roomTypeId) {
            return undefined;
        }

        return accommodationOptionRows.find(row =>
            row.roomTypeId === accommodationOptionForm.roomTypeId
            && (!accommodationOptionForm.allotmentId || row.allotmentId === accommodationOptionForm.allotmentId)
        );
    }, [accommodationOptionForm.allotmentId, accommodationOptionForm.roomTypeId, accommodationOptionRows]);

    const handleAccommodationOptionFormChange = (
        field: keyof AccommodationOptionFormState,
        value: string | number | null | undefined
    ) => {
        setAccommodationOptionForm(previous => ({
            ...previous,
            [field]: value == null ? "" : String(value)
        }));
    };

    const openAddAccommodationOptionForm = () => {
        setAccommodationOptionForm(createDefaultAccommodationOptionForm(
            selectedBasicSetupEvent?.startDate,
            selectedBasicSetupEvent?.endDate,
            getDefaultAccommodationHotelSelection()
        ));
        setIsAccommodationOptionFormOpen(true);
    };

    const openEditAccommodationOptionForm = (row: AccommodationOptionRow) => {
        shouldScrollToAccommodationOptionFormRef.current = true;
        setAccommodationOptionForm({
            allotmentId: row.allotmentId,
            roomTypeId: row.roomTypeId,
            hotelSelection: row.hotelId ? `${EVENT_HOTEL_SELECTION_PREFIX}${row.hotelId}` : getDefaultAccommodationHotelSelection(),
            roomTypeName: textValue(row.roomTypeName),
            capacity: String(row.capacity || "1"),
            pricePerNight: String(row.pricePerNight || "0"),
            description: textValue(row.description),
            startDate: dateInputValue(row.startDate) || dateInputValue(selectedBasicSetupEvent?.startDate),
            endDate: dateInputValue(row.endDate) || dateInputValue(selectedBasicSetupEvent?.endDate),
            totalRooms: String(row.totalRooms || "1"),
            paymentDeadline: dateInputValue(row.paymentDeadline)
        });
        setIsAccommodationOptionFormOpen(true);
    };

    useEffect(() => {
        if (!isAccommodationOptionFormOpen || !shouldScrollToAccommodationOptionFormRef.current) {
            return;
        }

        shouldScrollToAccommodationOptionFormRef.current = false;
        accommodationOptionFormRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "start"
        });
    }, [accommodationOptionForm, isAccommodationOptionFormOpen]);

    const accommodationOptionColumns: TableColumnsType<AccommodationOptionRow> = [
        {
            key: "hotel",
            title: t("hotel", {defaultValue: "Hotel"}),
            sorter: textSorter(row => row.hotelName),
            render: (_, row) => row.hotelName
        },
        {
            key: "roomType",
            title: t("room-type", {defaultValue: "Room type"}),
            sorter: textSorter(row => row.roomTypeName),
            render: (_, row) => row.roomTypeName
        },
        {
            key: "capacity",
            title: t("capacity", {defaultValue: "Capacity"}),
            sorter: numberSorter(row => row.capacity),
            render: (_, row) => row.capacity || "-"
        },
        {
            key: "pricePerNight",
            title: t("price-per-night", {defaultValue: "Price per night"}),
            sorter: numberSorter(row => row.pricePerNight),
            render: (_, row) => row.pricePerNight || "0"
        },
        {
            key: "period",
            title: t("period", {defaultValue: "Period"}),
            sorter: dateSorter(row => row.startDate),
            render: (_, row) => row.startDate && row.endDate
                ? `${formatLocalDate(row.startDate)} - ${formatLocalDate(row.endDate)}`
                : t("event-config-accommodation-period-missing", {defaultValue: "Period missing"})
        },
        {
            key: "totalRooms",
            title: t("total-rooms", {defaultValue: "Total rooms"}),
            sorter: numberSorter(row => row.totalRooms),
            render: (_, row) => row.totalRooms || "-"
        },
        {
            key: "reservationCount",
            title: t("reserved", {defaultValue: "Reserved"}),
            sorter: numberSorter(row => row.reservationCount),
            render: (_, row) => row.reservationCount
        },
        {
            key: "paymentDeadline",
            title: t("payment-deadline", {defaultValue: "Payment deadline"}),
            sorter: dateSorter(row => row.paymentDeadline),
            render: (_, row) => row.paymentDeadline ? formatLocalDate(row.paymentDeadline) : "-"
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, row) => (
                <Button onClick={() => openEditAccommodationOptionForm(row)}>
                    {t("edit", {defaultValue: "Edit"})}
                </Button>
            )
        }
    ];

    const closeAccommodationOptionForm = () => {
        setIsAccommodationOptionFormOpen(false);
        setAccommodationOptionForm(createDefaultAccommodationOptionForm(
            selectedBasicSetupEvent?.startDate,
            selectedBasicSetupEvent?.endDate,
            getDefaultAccommodationHotelSelection()
        ));
    };

    const handleDeleteAccommodationOptions = async (rows: AccommodationOptionRow[]) => {
        if (!eventNumericId || deletingAccommodationOptions) {
            return;
        }

        const roomTypeIds = Array.from(new Set(
            rows
                .map(row => row.roomTypeId)
                .filter((roomTypeId): roomTypeId is number => roomTypeId != null)
        ));
        if (roomTypeIds.length === 0) {
            return;
        }

        try {
            const result = await deleteAccommodationOptions({
                variables: {
                    input: {
                        eventId: eventNumericId,
                        roomTypeIds
                    }
                }
            });
            if (!result.data?.deleteAccommodationOptions) {
                throw new Error(t("error-occurred", {defaultValue: "An error occurred"}));
            }

            if (accommodationOptionForm.roomTypeId && roomTypeIds.includes(accommodationOptionForm.roomTypeId)) {
                closeAccommodationOptionForm();
            }
            setSelectedAccommodationOptionRowIds([]);
            message.success(t("event-config-accommodation-options-deleted", {
                defaultValue: "Accommodation options deleted"
            }));
            await Promise.all([
                roomTypesQuery.refetch(),
                allotmentsQuery.refetch()
            ]);
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
            throw error;
        }
    };

    const confirmDeleteAccommodationOptions = (rows: AccommodationOptionRow[]) => {
        const deletableRows = rows.filter(row => row.roomTypeId != null);
        if (deletableRows.length === 0) {
            message.warning(t("event-config-delete-accommodation-options-none", {
                defaultValue: "Select at least one room type to delete."
            }));
            return;
        }

        const rowsWithReservations = deletableRows.filter(row => row.reservationCount > 0);
        const warning = rowsWithReservations.length > 0
            ? t("event-config-delete-accommodation-options-reservations-warning", {
                options: rowsWithReservations
                    .map(row => `${row.hotelName} - ${row.roomTypeName}`)
                    .join(", "),
                defaultValue: "Reservations are linked to the following accommodation options: {{options}}. Deleting the options will also remove those reservations."
            })
            : undefined;
        const confirmation = buildDeleteConfirmation(t, {
            itemType: t("delete-target-accommodation-option", {defaultValue: "accommodation option"}),
            itemName: deletableRows.length === 1
                ? `${deletableRows[0].hotelName} - ${deletableRows[0].roomTypeName}`
                : undefined,
            count: deletableRows.length > 1 ? deletableRows.length : undefined,
            warning
        });

        modal.confirm({
            title: confirmation.title,
            content: confirmation.description,
            okText: confirmation.okText,
            cancelText: confirmation.cancelText,
            okButtonProps: {danger: true},
            onOk: () => handleDeleteAccommodationOptions(deletableRows)
        });
    };

    const refetchEventHotels = async () => {
        const refetchResult = await hotelsQuery.refetch();
        return refetchResult.data?.eventHotels?.items ?? hotelsQuery.data?.eventHotels?.items ?? [];
    };

    const handleHotelCatalogSaved = (createdHotel: EventHotelReturnDto) => {
        setInlineEventHotels(previous => mergeEventHotels(previous, [createdHotel]));
        setAccommodationOptionForm(previous => ({
            ...previous,
            hotelSelection: `${EVENT_HOTEL_SELECTION_PREFIX}${createdHotel.id}`
        }));
        setIsHotelCatalogFormOpen(false);
    };

    const handleSubmitAccommodationOption = async () => {
        if (!eventNumericId || savingAccommodationOption) {
            return;
        }

        const form = accommodationOptionForm;
        if (!form.roomTypeName.trim()) {
            message.error(t("event-config-room-type-name-required", {defaultValue: "Room type name is required."}));
            return;
        }

        const capacity = Number(form.capacity);
        const pricePerNight = Number(form.pricePerNight);
        const totalRooms = Number(form.totalRooms);
        if (!Number.isFinite(capacity) || capacity < 1 || !Number.isInteger(capacity)) {
            message.error(t("event-config-room-capacity-invalid", {defaultValue: "Room capacity must be a whole number greater than zero."}));
            return;
        }
        if (!Number.isFinite(totalRooms) || totalRooms < 1 || !Number.isInteger(totalRooms)) {
            message.error(t("event-config-total-rooms-invalid", {defaultValue: "Total rooms must be a whole number greater than zero."}));
            return;
        }
        if (!Number.isFinite(pricePerNight) || pricePerNight < 0) {
            message.error(t("event-config-accommodation-price-invalid", {defaultValue: "Price must be zero or greater."}));
            return;
        }

        const selectedHotel = parseAccommodationHotelSelection(form.hotelSelection);
        if (!selectedHotel || !Number.isInteger(selectedHotel.id)) {
            message.error(t("event-config-accommodation-hotel-required", {
                defaultValue: "Select a hotel from the catalog or add a new hotel first."
            }));
            return;
        }

        try {
            const result = await saveAccommodationOption({
                variables: {
                    input: {
                        eventId: eventNumericId,
                        hotelId: selectedHotel.type === "event" ? selectedHotel.id : null,
                        hotelIdentityId: selectedHotel.type === "catalog" ? selectedHotel.id : null,
                        roomTypeId: form.roomTypeId,
                        allotmentId: form.allotmentId,
                        name: form.roomTypeName.trim(),
                        capacity,
                        pricePerNight,
                        description: form.description,
                        startDate: formatRequiredLocalDateInput(form.startDate, t("start-date", {defaultValue: "Start date"})),
                        endDate: formatRequiredLocalDateInput(form.endDate, t("end-date", {defaultValue: "End date"})),
                        totalRooms,
                        paymentDeadline: formatOptionalLocalDateInput(form.paymentDeadline, t("payment-deadline", {defaultValue: "Payment deadline"}))
                    }
                }
            });
            if (!result.data?.saveAccommodationOption) {
                throw new Error(t("error-occurred", {defaultValue: "An error occurred"}));
            }

            message.success(t("event-config-accommodation-option-saved", {defaultValue: "Accommodation option saved"}));
            closeAccommodationOptionForm();
            await Promise.all([
                hotelsQuery.refetch(),
                hotelIdentityCatalogQuery.refetch(),
                roomTypesQuery.refetch(),
                allotmentsQuery.refetch()
            ]);
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <div className="ati-stack">
            <PageHeader level={2} title={t("setup-sequence.accommodation-title", {defaultValue: "Accommodation"})}/>
            <Panel title={t("event-config-guided-accommodation", {defaultValue: "Accommodation setup"})}>
                <div className="ati-stack">
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={openAddAccommodationOptionForm}
                        >
                            {t("event-config-add-accommodation-option", {defaultValue: "Add accommodation option"})}
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined/>}
                            disabled={selectedAccommodationOptionRows.length === 0}
                            loading={deletingAccommodationOptions}
                            onClick={() => confirmDeleteAccommodationOptions(selectedAccommodationOptionRows)}
                        >
                            {t("delete", {defaultValue: "Delete"})}
                        </Button>
                    </Space>
                    {isAccommodationOptionFormOpen && (
                        <div
                            ref={accommodationOptionFormRef}
                            className="event-config-accommodation-option-form"
                        >
                            <Panel
                                flat
                                title={accommodationOptionForm.roomTypeId
                                    ? t("event-config-edit-accommodation-option", {defaultValue: "Edit accommodation option"})
                                    : t("event-config-new-accommodation-option", {defaultValue: "New accommodation option"})}
                            >
                                <Form layout="vertical">
                                    <Row gutter={16}>
                                        <Col xs={24} md={16}>
                                            <Form.Item
                                                label={t("event-config-accommodation-hotel-select", {defaultValue: "Hotel from global catalog"})}
                                            >
                                                <Select
                                                    showSearch
                                                    optionFilterProp="label"
                                                    value={accommodationOptionForm.hotelSelection || undefined}
                                                    onChange={value => handleAccommodationOptionFormChange("hotelSelection", value)}
                                                    options={accommodationHotelSelectionOptions}
                                                    disabled={hotelIdentityCatalogQuery.loading}
                                                    placeholder={t("event-config-accommodation-hotel-select-placeholder", {
                                                        defaultValue: "Choose hotel from catalog"
                                                    })}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item
                                                label={t("event-config-accommodation-new-hotel", {defaultValue: "New hotel in catalog"})}
                                            >
                                                <Button onClick={() => setIsHotelCatalogFormOpen(true)} disabled={!eventNumericId}>
                                                    {t("event-config-add-hotel-to-catalog", {defaultValue: "Add hotel to catalog"})}
                                                </Button>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item label={t("room-type", {defaultValue: "Room type"})}>
                                                <Input
                                                    value={accommodationOptionForm.roomTypeName}
                                                    onChange={event => handleAccommodationOptionFormChange("roomTypeName", event.target.value)}
                                                    placeholder={t("event-config-room-type-name-placeholder", {defaultValue: "Room type name"})}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("capacity", {defaultValue: "Capacity"})}>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    value={accommodationOptionForm.capacity}
                                                    onChange={event => handleAccommodationOptionFormChange("capacity", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("total-rooms", {defaultValue: "Total rooms"})}>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    value={accommodationOptionForm.totalRooms}
                                                    onChange={event => handleAccommodationOptionFormChange("totalRooms", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("price-per-night", {defaultValue: "Price per night"})}>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    value={accommodationOptionForm.pricePerNight}
                                                    onChange={event => handleAccommodationOptionFormChange("pricePerNight", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("start-date", {defaultValue: "Start date"})}>
                                                <Input
                                                    type="date"
                                                    value={accommodationOptionForm.startDate}
                                                    onChange={event => handleAccommodationOptionFormChange("startDate", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("end-date", {defaultValue: "End date"})}>
                                                <Input
                                                    type="date"
                                                    value={accommodationOptionForm.endDate}
                                                    onChange={event => handleAccommodationOptionFormChange("endDate", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={6}>
                                            <Form.Item label={t("payment-deadline", {defaultValue: "Payment deadline"})}>
                                                <Input
                                                    type="date"
                                                    value={accommodationOptionForm.paymentDeadline}
                                                    onChange={event => handleAccommodationOptionFormChange("paymentDeadline", event.target.value)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label={t("description", {defaultValue: "Description"})}>
                                                <Input.TextArea
                                                    value={accommodationOptionForm.description}
                                                    onChange={event => handleAccommodationOptionFormChange("description", event.target.value)}
                                                    rows={3}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Space wrap>
                                        <Button
                                            type="primary"
                                            onClick={handleSubmitAccommodationOption}
                                            loading={savingAccommodationOption}
                                        >
                                            {t("event-config-save-accommodation-option", {defaultValue: "Save accommodation option"})}
                                        </Button>
                                        {editedAccommodationOptionRow && (
                                            <Button
                                                danger
                                                icon={<DeleteOutlined/>}
                                                loading={deletingAccommodationOptions}
                                                onClick={() => confirmDeleteAccommodationOptions([editedAccommodationOptionRow])}
                                            >
                                                {t("delete", {defaultValue: "Delete"})}
                                            </Button>
                                        )}
                                        <Button onClick={closeAccommodationOptionForm}>
                                            {t("cancel", {defaultValue: "Cancel"})}
                                        </Button>
                                    </Space>
                                </Form>
                                <HotelCatalogModal
                                    open={isHotelCatalogFormOpen}
                                    eventId={eventNumericId}
                                    errorLabels={errorLabels}
                                    refetchEventHotels={refetchEventHotels}
                                    onClose={() => setIsHotelCatalogFormOpen(false)}
                                    onSaved={handleHotelCatalogSaved}
                                />
                            </Panel>
                        </div>
                    )}
                    <Table<AccommodationOptionRow>
                        rowKey="rowId"
                        columns={accommodationOptionColumns}
                        dataSource={accommodationOptionRows}
                        rowSelection={{
                            selectedRowKeys: selectedAccommodationOptionRowIds,
                            onChange: selectedRowKeys => setSelectedAccommodationOptionRowIds(selectedRowKeys),
                            preserveSelectedRowKeys: false
                        }}
                        pagination={false}
                        scroll={{x: 900}}
                        locale={{
                            emptyText: (
                                <Text type="secondary">
                                    {t("event-config-accommodation-options-empty", {
                                        defaultValue: "No accommodation options yet."
                                    })}
                                </Text>
                            )
                        }}
                    />
                </div>
            </Panel>
        </div>
    );
};

export default EventAccommodationPage;
